/**
 * Store carousel manifest — one entry per game slide.
 *
 * Live games carry all three art slots (character overlay PNG, card background,
 * wordmark SVG). Coming-soon / mystery games may have null slots — the Store
 * renders grey placeholder boxes at those slots. The user's future art drops in
 * at the same manifest paths with ZERO code change.
 *
 * Vite resolves the imported URLs at build time, so these are real hashed asset
 * URLs in production.
 */

import { GAME_RULES, type GameId } from '@ringmaster/shared';
import bloodmoonCharacter from './games/wolf-character.webp';
import bloodmoonBg from './games/bloodmoon-bg.webp';
import bloodmoonWordmark from './games/bloodmoon.svg';
import bloodmoonCardBack from './games/bloodmoon-card-back.webp';
import bloodmoonCardFront from './games/bloodmoon-card-front.webp';
// The Whisper's own ornate card back — violet all-seeing eye on indigo tapestry
// ("Seer's Tapestry"), same portrait ratio family as the Blood Moon back.
import whisperCardBack from './cosmetics/cardback-arcana.webp';
// One Night's own card back — same family as Blood Moon's, distinct enough to
// read as a variant, not a reskin. See ui/roleCardDefs.ts ONE_NIGHT_CARD_BACK
// (the actual prop wired into HoloRoleCard/PeekRevealCard/centre-card slots).
import oneNightCardBack from './cosmetics/cardback-eclipse.webp';
// Omertà is not yet playable — use the desaturated "coming soon" art.
import omertaCharacter from './games/omerta-character-gray.webp';
import omertaBg from './games/omerta-bg-gray.webp';
import omertaWordmark from './games/omerta.svg';
// The Whisper — real art. Character reuses the existing Seer we already have.
import whisperCharacter from './cards/seer-character.png';
import whisperBg from './games/whisper-bg.png';
import whisperWordmark from './games/whisper.svg';

export type GameStatus = 'live' | 'coming-soon';

// The player bands live in the shared contract (one registry, both games) — the
// manifest is art + copy only, so the "which game fits this crowd" query below
// reads GAME_RULES rather than duplicating any numbers.


export interface GameManifestEntry {
  id: string;
  name: string;
  subtitle: string;
  /** Display string, e.g. "5–10". */
  players: string;
  /** Display string, e.g. "~20 min". */
  time: string;
  status: GameStatus;
  /** Transparent, oversized character overlay PNG (or null → grey box). */
  character: string | null;
  /** Card background image (or null → grey box). */
  cardBg: string | null;
  /** Wordmark title SVG (or null → text fallback). */
  wordmark: string | null;
  /** Ornate card-back image for the hidden/unrevealed role card (or null). */
  roleCardBack: string | null;
  /** Ornate empty-frame image for the revealed role card (or null). */
  roleCardFront: string | null;
  /**
   * Dark banner art for the shared GameHeader (or null → CSS gradient + crimson
   * moon-glow fallback). Real art drops in here later with zero code change.
   */
  headerArt: string | null;
  /** CSS gradient for the card accent (design-token derived). */
  accentGradient: string;
  /** Optional marketing badge shown on the store card ("Popular", "BETA"). */
  badge?: string;
}

export const GAME_MANIFEST: GameManifestEntry[] = [
  {
    id: 'bloodmoon',
    name: 'Blood Moon',
    subtitle: 'A werewolf game',
    players: '5–10',
    time: '~10 min',
    status: 'live',
    badge: 'Popular',
    character: bloodmoonCharacter,
    cardBg: bloodmoonBg,
    wordmark: bloodmoonWordmark,
    roleCardBack: bloodmoonCardBack,
    roleCardFront: bloodmoonCardFront,
    headerArt: null,
    accentGradient: 'linear-gradient(135deg, #802636 0%, #990000 100%)',
  },
  {
    // The Whisper — second live game. Real Seer's-tent art: the new wordmark +
    // background, with the existing Seer as the character overlay. The role
    // card BACK is the Whisper's own arcana tapestry (cardback-arcana.webp);
    // the FRONT frame still reuses the Blood Moon frame (functional placeholder
    // — a dedicated Whisper front can drop in at this slot with zero code
    // change). The card's create flow carries gameId:'whisper' — see
    // routes/Store.tsx GameCard + routes/Host.tsx.
    id: 'whisper',
    name: 'The Whisper',
    subtitle: 'A séance word-hunt',
    players: '3–7',
    time: '~5 min',
    status: 'live',
    badge: 'BETA',
    character: whisperCharacter,
    cardBg: whisperBg,
    wordmark: whisperWordmark,
    roleCardBack: whisperCardBack,
    roleCardFront: bloodmoonCardFront,
    headerArt: null,
    // Arcana ramp — violet mysticism (--arcana-1 → --arcana-3). No blood accent.
    accentGradient: 'linear-gradient(135deg, #2c1650 0%, #8b5cf6 100%)',
  },
  {
    // Blood Moon: One Night — a Blood Moon variant (3–7 players, one night,
    // one vote). Borrows Blood Moon's own character/cardBg/card-front art as a
    // functional placeholder — it shares that game's crimson skin, so this is
    // the same "reuse, don't invent new asset files" precedent as the
    // Whisper's card FRONT frame above. TODO(onenight): dedicated art + a
    // wordmark distinct from the flagship's. The card BACK, however, is its
    // own — cardback-eclipse.webp — so the variant reads as visually distinct
    // from the flagship while staying in the same family.
    //
    // LIVE but badged BETA: the engine + e2e suite are green, however the
    // balance sim reads wolf-sided (~27-32% village across 3–7) and no bot in it
    // models how a real table cross-checks role claims. It needs human playtests
    // before it loses the BETA badge — see packages/engine/scripts/onenight-balance.ts.
    id: 'onenight',
    name: 'Blood Moon: One Night',
    subtitle: 'One night. One vote. No mercy.',
    players: '3–7',
    time: '~10 min',
    status: 'live',
    badge: 'BETA',
    character: bloodmoonCharacter,
    cardBg: bloodmoonBg,
    wordmark: null,
    roleCardBack: oneNightCardBack,
    roleCardFront: bloodmoonCardFront,
    headerArt: null,
    accentGradient: 'linear-gradient(135deg, #802636 0%, #990000 100%)',
  },
  {
    id: 'omerta',
    name: 'Omertà',
    subtitle: 'A classic mafia game',
    players: '6–12',
    time: '~25 min',
    status: 'coming-soon',
    character: omertaCharacter,
    cardBg: omertaBg,
    wordmark: omertaWordmark,
    roleCardBack: null,
    roleCardFront: null,
    headerArt: null,
    accentGradient: 'linear-gradient(135deg, #2b2b2b 0%, #6e6e6e 100%)',
  },
  {
    id: 'mystery1',
    name: 'Coming soon',
    subtitle: 'A new game, still brewing',
    players: '—',
    time: '—',
    status: 'coming-soon',
    character: null,
    cardBg: null,
    wordmark: null,
    roleCardBack: null,
    roleCardFront: null,
    headerArt: null,
    accentGradient: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
  },
  {
    id: 'mystery2',
    name: 'Coming soon',
    subtitle: 'A new game, still brewing',
    players: '—',
    time: '—',
    status: 'coming-soon',
    character: null,
    cardBg: null,
    wordmark: null,
    roleCardBack: null,
    roleCardFront: null,
    headerArt: null,
    accentGradient: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
  },
];

export function gameById(id: string): GameManifestEntry | undefined {
  return GAME_MANIFEST.find((g) => g.id === id);
}

/** A catalogued, playable game whose player band contains a given headcount. */
export interface GameFit {
  id: GameId;
  name: string;
  minPlayers: number;
  maxPlayers: number;
}

/**
 * The best *other* shipped game for a table of `count` players — the lobby's
 * "you can't fill Blood Moon, but The Whisper fits right now" nudge (logs show a
 * 4-player Blood Moon lobby sitting on "Need 1 more player" for 1h51m).
 *
 * Derived, never hard-coded: player bands come from the shared GAME_RULES
 * registry (so a new game with a new band is picked up for free) and playability
 * from this manifest's `status` — a "coming soon" tile is never suggested. Ties
 * break toward the LARGEST minPlayers that still fits, i.e. the richest game the
 * current crowd can actually run. Returns null when nothing else fits.
 */
export function suggestGameForCount(count: number, currentGameId: string): GameFit | null {
  const best = Object.values(GAME_RULES)
    .filter(
      (rules) =>
        rules.id !== currentGameId &&
        count >= rules.minPlayers &&
        count <= rules.maxPlayers &&
        // `offered` is the product fact (shared/games.ts) — a retired game must
        // never be nudged toward. Manifest `status` stays in the test as the
        // ART fact: a tile with no card background can't be shown either way.
        rules.offered &&
        gameById(rules.id)?.status === 'live',
    )
    .sort((a, b) => b.minPlayers - a.minPlayers)[0];
  if (!best) return null;
  return {
    // GAME_RULES is keyed by GameId; `id` is that same literal.
    id: best.id as GameId,
    name: gameById(best.id)?.name ?? best.id,
    minPlayers: best.minPlayers,
    maxPlayers: best.maxPlayers,
  };
}
